import React from 'react';
import { motion } from 'framer-motion';
import { MdPhotoSizeSelectActual } from 'react-icons/md';

interface LandingPageProps {
  onGetStarted: () => void;
  onTryDemo: () => void;
  theme: string;
  onToggleTheme: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({
  onGetStarted,
  onTryDemo,
  theme,
  onToggleTheme
}) => {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-900 text-slate-900 dark:text-slate-50">
      {/* Header with theme toggle */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-primary dark:text-primary">Get Deleto</h1>
            <div className="flex items-center gap-4">
              <button
                onClick={onToggleTheme}
                className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 flex items-center justify-center overflow-hidden relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50"
                title="Change Theme"
                aria-label="Change Theme"
              >
                <MdPhotoSizeSelectActual className="w-5 h-5 text-gray-600 dark:text-gray-300 group-hover:text-primary" />
              </button>
              <a
                href="#/"
                className="text-primary hover:text-primary-dark transition-colors font-medium"
              >
                Go to App →
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 md:py-12">
        <div className="max-w-screen-xl mx-auto px-4 text-center space-y-4">
          
          {/* Hero Section */}
          <div className="animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold text-primary dark:text-primary mb-6">
              GET DELETO
            </h1>
            <p className="text-xl md:text-2xl text-black dark:text-white font-medium mb-4">
            <strong>Write first, edit later.</strong> Pause for too long and your words <b>vanish.</b>
            </p>
            
            <div className="flex justify-center mb-4">
              <ul className="text-left text-xl md:text-2xl space-y-1 leading-relaxed text-black dark:text-white">
                <li>• Write without overthinking</li>
                <li>• Step into flow anytime</li>
                <li>• Create without restraint</li>
              </ul>
            </div>
            
            {/* Gradient Divider Bar */}
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full mx-auto"></div>
          </div>

          {/* Hero Component - Video + Copy */}
          <div className="animate-slide-up delay-200">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
              
              {/* Demo Video */}
              <div className="p-0 mb-6">
                <p className="text-center text-xl md:text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-6">
                  See it in action 📽️
                </p>
                <div className="flex justify-center">
                  <video 
                    src="/GetDeleto_demo.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                    preload="metadata"
                    className="w-full max-w-[1000px] h-auto rounded-lg shadow-lg"
                    onError={(e) => {
                      console.error('Video failed to load:', e);
                      const target = e.target as HTMLVideoElement;
                      target.style.display = 'none';
                      const errorMsg = target.parentElement?.querySelector('.video-error');
                      if (errorMsg) {
                        (errorMsg as HTMLElement).style.display = 'block';
                      }
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                  <div 
                    className="video-error text-center p-8 text-gray-600 dark:text-gray-400" 
                    style={{ display: 'none' }}
                  >
                    <p className="mb-2">🎥 Demo video temporarily unavailable</p>
                    <p className="text-sm">The concept: Stop typing for 6 seconds and your text gets deleted!</p>
                  </div>
                </div>
              </div>

              {/* Copy & CTA */}
              <div className="space-y-4 text-center">
                {/* CTA Section */}
                <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                  <motion.button
                    onClick={onGetStarted}
                    className="btn btn-primary text-lg px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 1.5, delay: 3, repeat: 1 }}
                  >
                    Start 3-min Session
                  </motion.button>
                </div>
              </div>

            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 shadow-sm mt-16">
        <div className="container mx-auto px-4 py-6">
          <div className="text-center text-gray-600 dark:text-gray-400">
            <p>
              © 2025 Get Deleto. Built for writers who overthink.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 